import React, {useEffect, useState} from 'react';
import {Controller, useForm} from "react-hook-form";
import {NavLink, useNavigate} from "react-router-dom";
import {route} from "../../../models/route";
import {Require} from "../Require";
import {scrollTo} from "../../../service/ToolsService";
import {ApiError, ApiQueryParam, ApiService} from "../../../service/ApiService";
import {circuitResultCollectionType, circuitResultType} from "../../../types/Api/CircuitResultType";
import {Loader} from "../Loader";
import Select from 'react-select';

type OptionType = {
  value: string;
  label: string;
};

type FormDataType = {
  rideDate?: string,
  meetPlace?: string,
  meetHour?: string,
  startHour?: string,
  circuit?: OptionType,
}

type RouteFormProps = {
  route?: route,
}

export const RouteForm = ({route}: RouteFormProps) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [circuitOptions, setCircuitOptions] = useState([] as OptionType[]);

  useEffect(() => {
    const apiFilters: ApiQueryParam = new ApiQueryParam();

    apiFilters.addSort({by: 'label', direction: 'ASC'})
    apiFilters.setCount(0);

    const resultCircuit = ApiService.get(`/circuit`, apiFilters) as Promise<circuitResultCollectionType>;

    setLoading(true);

    resultCircuit
      .then((r) => {
        setCircuitOptions(r._embedded.circuits.map((circuit: circuitResultType) => {
          return {
            value: circuit.circuitId,
            label: circuit.label,
          };
        }));
      })
      .catch(() => {
        alert("Une erreur c'est produite.")
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const {
    control,
    register,
    handleSubmit,
    setError,
    formState: {errors}
  } = useForm<FormDataType>({
    defaultValues: {
      rideDate: route?.rideDate,
      meetPlace: route?.meetPlace,
      meetHour: route?.meetHour,
      startHour: route?.startHour,
      circuit: route?.circuit ? {value: route?.circuit?.circuitId, label: route?.circuit?.label} : undefined,
    }
  });

  const onSubmit = async (data: FormDataType) => {
    let meetHour: string = data.meetHour ?? '';
    let startHour: string = data.startHour ?? '';

    if (meetHour.split(':').length === 2) {
      meetHour += ':00';
    }

    if (startHour.split(':').length === 2) {
      startHour += ':00';
    }

    const r = {
      rideDate: data.rideDate,
      meetPlace: data.meetPlace,
      meetHour: meetHour,
      startHour: startHour,
      circuitId: data.circuit?.value || undefined
    };

    try {
      route
        ? await ApiService.patch(`/route/${route.routeId}`, {param: () => r})
        : await ApiService.post(`/route`, {param: () => r})

      navigate('/admin/route/route');
      scrollTo();
    } catch (e: any) {
      const error: ApiError = e as ApiError;

      error.errors?.forEach((e: {
        field: string,
        message: string
      }) => {
        setError(e.field as "rideDate" | "meetPlace" | "meetHour" | "startHour" | "circuit", {
          type: "manual",
          message: e.message
        })
      });
    }
  }

  return (
    <>
      {loading ? (<Loader/>) :
        (
          <form className="p-3" method="post" onSubmit={handleSubmit(onSubmit)}>
            <div className="d-flex">
              <div className="col max-w600px pe-4">
                <div className="mb-3">
                  <label className="form-label" htmlFor="form-ride-date">Date de la sortie <Require/></label>

                  <input type="date" className="form-control max-w200px" id="form-ride-date" {...register('rideDate', {
                    required: 'La date de la sortie est obligatoire',
                  })}
                  />
                  {errors.rideDate && <p className="text-danger">{errors.rideDate.message}</p>}
                </div>
                <div className="mb-3">
                  <label className="form-label" htmlFor="form-meet-place">Lieux du rendez-vous <Require/></label>
                  <input id="form-meet-place" type="text" className="form-control" placeholder="~"
                         {...register("meetPlace", {
                           // required: "Le lieux du rendez-vous est obligatoire.",
                           validate: {
                             minLength: (value) =>
                               (value ?? "").length <= 255 || "Le lieux du rendez-vous ne doit pas dépasser 255 caractères",
                           },
                         })}
                  />
                  {errors.meetPlace && <p className="text-danger">{errors.meetPlace.message}</p>}
                </div>
                <div className="mb-3 d-flex flex-nowrap">
                  <div className="col">
                    <label className="form-label" htmlFor="form-meet-hour">Heure du rendez-vous <Require/></label>

                    <input type="time" className="form-control w100px" id="form-meet-hour" {...register('meetHour', {
                      required: 'L\'heure du rendez-vous est obligatoire',
                    })}
                    />
                    {errors.meetHour && <p className="text-danger">{errors.meetHour.message}</p>}
                  </div>
                  <div className="col">
                    <label className="form-label" htmlFor="form-start-hour">Heure de départ <Require/></label>

                    <input type="time" className="form-control w100px" id="form-start-hour" {...register('startHour', {
                      required: 'L\'heure de départ est obligatoire',
                    })}
                    />
                    {errors.startHour && <p className="text-danger">{errors.startHour.message}</p>}
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label" htmlFor="form-meetPlace">Circuit <Require/></label>
                  <Controller
                    name="circuit"
                    control={control}
                    defaultValue={undefined}
                    rules={{required: 'Le circuit est obligatoire'}}
                    render={({field}) => (
                      <Select
                        {...field}
                        options={circuitOptions}
                        placeholder="~"
                        isClearable
                        value={field.value}
                        onChange={(val) => field.onChange(val)}
                        filterOption={(option, inputValue) => option.label.toLowerCase().includes(inputValue.toLowerCase())}
                      />
                    )}
                  />
                  {errors.circuit && <p className="text-danger">{errors.circuit.message}</p>}
                </div>
              </div>
            </div>

            <div className="mt-3">
              <button className="btn btn-sm btn-success me-1" type="submit">Enregistrer</button>
              <NavLink to={`/admin/route/route`} onClick={scrollTo} className="btn btn-sm btn-info">Retour aux circuit</NavLink>
            </div>
          </form>
        )
      }
    </>
  );
}
